import BaseUISystem from '../BaseUISystem';
import BaseMenu from './BaseMenu';
import GameObject, { GameObjectEvent } from '../../../GameObject/GameObject';
import { UIActions } from '../../../State/UI/UIActions';
import Armature from '../../../Prototype/Armature';
import { PayerReactivationLayout } from '../../../Layouts/PayerReactivationPopup/PayerReactivationLayout';
import TransformComponent from '../../../Component/Transform';
import { applyDefaultButtonAnimation } from '../UIHelper';
import Game from '../../../Game';
import { GameActions } from '../../../State/Game/GameActions';
import { ContextType } from '../../../State/Game/ContextType';
import { IAPReward } from '../../../Utility/RewardHelper';
import TextComponent from '../../../Component/Render/Text';
import DateHelper from '../../../Utility/DateHelper';
import { TickerEvent } from '../../../Utility/Ticker';
import LevelHelper from '../../../Utility/LevelHelper';
import { IAPProduct } from '../../../Utility/SDK/Drivers/IAPCatalog';
import { formatStrikePrice } from '../../../Utility/Currency';
import Renderable from '../../../Component/Renderable';
import { IAPStateHelper } from 'State/Player/IAP/IAPStateHelper';
import { PopupID } from '../../../State/UI/PopupID';
import { ProductId } from 'Utility/Product';
import RelativePositionComponent from 'Component/Render/RelativePosition';
import Point from 'Utility/Point';
import { PlayerActions } from 'State/Player/PlayerActions';
import { Layout } from '../../../Utility/layout/createLayout';
import { report } from 'report';
import { formatWithDecimals } from 'Utility/Format';
import { IAPHelper } from 'Utility/Helpers/IAP/IAPHelper';

export default class PayerReactivationHandler extends BaseMenu {
    public static COOLDOWN_TIME: number = DateHelper.Hour * 72;
    public static OFFER_TIME: number = DateHelper.Hour * 12;

    private armature: Armature;
    private layout: any;
    private iapReward: IAPReward;
    private iapData: IAPProduct;

    private currentID: ProductId;

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.PAYER_REACTIVATION_MENU, uiSystem, false);
    }

    public onCreate() {
        super.onCreate();

        this.layout = new PayerReactivationLayout();
        this.layout.root.addComponent(RelativePositionComponent);
        this.layout.root.getComponent(RelativePositionComponent).relativePosition = new Point(0.5, 0.5);
        this.element.addChild(this.layout.root);

        this.armature = GameObject.Instantiate(Armature, {
            dragonbonesName: 'limited_offer_1',
            armatureName: 'Armature',
            animationName: 'animtion0',
        });
        this.layout.armatureContainer.addChild(this.armature);

        this.layout.title.getComponent(TextComponent).text = Game.locale.getText('LimitedTimeOffer').toUpperCase();

        this.addGradient(this.layout.title);
        applyDefaultButtonAnimation(this.layout.buyButton);
        applyDefaultButtonAnimation(this.layout.closeButton);

        const oldPriceTransform = this.layout.oldPrice.getComponent(TransformComponent);
        this.layout.strike.getComponent(TransformComponent).width = oldPriceTransform.width;

        this.layout.closeButton.once(GameObjectEvent.SELECTED, this.handleCloseClick);
        this.layout.buyButton.on(GameObjectEvent.SELECTED, this.handleBuyClick);
        // TODO: Use Timer here instead of ticker as timers are passed the game state on tick (removing the need to get gameState every tick)
        Game.ticker.on(TickerEvent.SECONDS_INTERVAL, this.handleTick);

        this.cleanupManager.add(() => {
            this.layout.buyButton.off(GameObjectEvent.SELECTED, this.handleBuyClick);
            Game.ticker.off(TickerEvent.SECONDS_INTERVAL, this.handleTick);
        });
    }

    private setPrices(): void {
        if (!this.iapData) {
            report('Payer Reactivation IAP data not found', {
                once: true,
                sampleRate: 0.1,
                fingerprint: 'PayerReactivationHandler:setPrices',
            });
            return;
        }

        this.layout.priceText.getComponent(TextComponent).text = this.iapData.price;

        const levelCostFactor = LevelHelper.getLevelCost(Game.player.getHighestLevelCompleted()) / 1000;
        const strikePriceUsd = this.iapReward.usdPriceWithoutDiscount + this.iapReward.usdMultiplier * levelCostFactor;
        const strikePrice = formatStrikePrice({
            price: this.iapData.price,
            priceUsd: this.iapReward.usdPrice,
            strikePriceUsd,
        });

        this.layout.oldPrice.getComponent(TextComponent).text = strikePrice;

        if (!strikePrice) {
            this.layout.strike.getComponent(Renderable).visible = false;
            this.layout.priceText.container.y = 0;
        }
    }

    public onShow(params?: { offerKey: ProductId }) {
        super.onShow();

        this.currentID = params.offerKey;

        this.iapReward = IAPHelper.CACHED_IAP_REWARDS_MAP()[this.currentID];

        this.iapData = Game.sdk.getCatalog().products.find(e => e.productId === this.currentID);

        this.setPrices();

        this.setRewardTexts();
    }

    private setRewardTexts(): void {
        this.layout.coinsText.getComponent(TextComponent).text = formatWithDecimals(
            this.iapReward.rewards[0].value,
            ','
        );
        this.layout.jokerText.getComponent(TextComponent).text = 'x' + this.iapReward.rewards[1].value;
        this.layout.plusFiveText.getComponent(TextComponent).text = 'x' + this.iapReward.rewards[2].value;
        this.layout.undoText.getComponent(TextComponent).text = 'x' + this.iapReward.rewards[3].value;
    }

    public onHidden() {
        this.armature?.destroy();
    }

    private readonly handleCloseClick = (): void => {
        UIActions.hidePopup(this.name);
    };

    private readonly handleBuyClick = (): void => {
        UIActions.hidePopup(this.name);
        GameActions.setContext(ContextType.PURCHASE_PRODUCT, {
            product: this.iapReward,
        });
        Game.store.waitFor(GameActions.restoreContext).then(() => {
            const state = Game.store.getState();
            Game.store.dispatch(
                PlayerActions.setPlayerState(
                    IAPStateHelper.trackPayerReactivationPurchase(state, this.currentID).playerState
                )
            );
        });
    };

    private readonly handleTick = (): void => {
        const shownAt = IAPStateHelper.getPayerReactivationOfferStart(Game.store.getState());
        const timeLeft = Math.max(0, shownAt + PayerReactivationHandler.OFFER_TIME - DateHelper.now.getTime());

        if (timeLeft === 0) {
            UIActions.hidePopup(this.name);
            return;
        }

        this.layout.timeText.getComponent(TextComponent).text = DateHelper.format(timeLeft, 'hh:mm:ss');
    };

    private addGradient(gameObject: GameObject<PIXI.Text>): void {
        const { style } = gameObject.getComponent(TextComponent);
        style.fill = ['#FFFFFF', '#FFFFFF', '#FFE881', '#FFFFFF'];
        style.fillGradientStops = [0, 0.5, 0.5, 1];
        style.fillGradientType = 0;
    }
}
